import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card, Table, Button, Space, Input, InputNumber, Select, Typography, App,
  Modal, Spin, Popover, Tooltip, Tag,
} from 'antd';
import { JobStatusTag } from '@components/StatusTag';
import ApiCodeLink from '@components/ApiCodeLink';
import { ArrowLeftOutlined, SearchOutlined, CopyOutlined, MoreOutlined, LockFilled, ThunderboltOutlined, CheckSquareOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import CreatePredictionUpdateJobModal from '@components/CreatePredictionUpdateJobModal';
import RerunFromQueryConfirmModal from '../JobTaskList/RerunFromQueryConfirmModal';
import { extractNonEmptyFilters, toQueryFilters } from '../JobTaskList/filterLabels';
import {
  getTaskList,
  getTaskStatusList,
  getTaskLogs,
  getPredictionUpdateJobs,
  getPredictionUpdateTaskIterations,
  type JobItem,
  type TaskListItem,
  type TaskListParams,
  type StatusOption,
  type TaskLogResponse,
  type PredictionIterState,
} from '@/apis/simulation';

const { Title, Text } = Typography;

const formatTime = (val: string | null | undefined) =>
  val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-';

// 迭代状态四档判定用的 TaskStatusEnum 终态集合：
// cancelled(1)/succeeded(15)/not_pass(20)/failed(25)/unknown(30)/coredump(40)。
// status==='succeeded' → 已迭代（绿）；status==='failed' → 迭代失败（红，hover 看原因）；
// map 无该 task 且已终态 → 未迭代（灰）；未终态 → 进行中（蓝）。
const PREDICTION_TERMINAL_STATUSES = new Set([1, 15, 20, 25, 30, 40]);

/**
 * 预测数据迭代（PR_DATA）Job 下的 Task 列表 —— 独立自实现页，不复用通用 JobTaskList。
 *
 * 相比通用任务列表，本页刻意去掉：评估器级别筛选、基于筛选项创建任务、按场景分类、
 * 未通过页面跳转、任务对比按钮 —— 只保留干净任务表 + 基础查询 / 分页，并额外挂
 * 「迭代状态」「迭代版本号」两列（数据来自 /prediction-update/task-iterations 旁挂接口）。
 */
export default function PredictionUpdateJobTasksPage() {
  const { jobId: jobIdParam } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();

  const jobId = jobIdParam ? Number(jobIdParam) : NaN;

  const [jobName, setJobName] = useState<string>('');
  /** Job 级调度 ID（compute-flow），task 无 taskDispatchId 时展示 */
  const [jobDispatchId, setJobDispatchId] = useState<string>('');
  const [data, setData] = useState<TaskListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusList, setStatusList] = useState<StatusOption[]>([]);

  // {taskId(字符串): 迭代状态对象 {version, status, reason}}，job 维度一次拉全
  // （翻页/筛选不重拉，迭代产出与分页无关）；供「迭代状态」「迭代版本号」两列渲染。
  const [predictionIterMap, setPredictionIterMap] = useState<Record<string, PredictionIterState>>({});

  const [page, setPage] = useState({ current: 1, pageSize: 10 });
  const [filterTaskId, setFilterTaskId] = useState<number | undefined>();
  const [filterSceneName, setFilterSceneName] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | number | undefined>();

  const [taskLogModalVisible, setTaskLogModalVisible] = useState(false);
  const [taskLogLoading, setTaskLogLoading] = useState(false);
  const [taskLogData, setTaskLogData] = useState<TaskLogResponse | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null);

  // ── 「发起任务」入口:手动勾选 / 按当前查询 ────────────────────────────────
  // 两条路径都由后端按场景新建匿名场景集叶子(job.scene_set_ids 只写叶子 id),
  // 走「按场景集发起」下发;手动勾选传 sceneIds,查询路径传 filters 由后端反查。
  const [jobMeta, setJobMeta] = useState<JobItem | null>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  // taskId → sceneId:翻页/跨页保留勾选时,antd 只回传 key,用它换场景 id。
  const [sceneIdByTaskId, setSceneIdByTaskId] = useState<Record<number, number>>({});
  const [rerunSource, setRerunSource] = useState<'selected' | 'filter' | null>(null);
  const [rerunConfirmOpen, setRerunConfirmOpen] = useState(false);
  const [rerunCreateOpen, setRerunCreateOpen] = useState(false);

  const loadStatusList = useCallback(async () => {
    try {
      const list = await getTaskStatusList();
      setStatusList(list);
    } catch {
      message.error('加载状态列表失败');
    }
  }, [message]);

  const buildParams = useCallback(
    (current: number, pageSize: number): TaskListParams => ({
      jobId,
      current,
      pageSize,
      ...(filterTaskId != null && !Number.isNaN(filterTaskId) ? { taskId: filterTaskId } : {}),
      ...(filterSceneName.trim() ? { sceneName: filterSceneName.trim() } : {}),
      ...(filterStatus !== undefined && filterStatus !== '' ? { status: filterStatus } : {}),
    }),
    [jobId, filterTaskId, filterSceneName, filterStatus],
  );

  const loadTaskList = useCallback(
    async (current: number, pageSize: number) => {
      if (!jobId || Number.isNaN(jobId)) {
        message.warning('无效的 Job ID');
        return;
      }
      setLoading(true);
      try {
        const res = await getTaskList(buildParams(current, pageSize));
        setData(res.tasks);
        setSceneIdByTaskId((prev) => {
          const next = { ...prev };
          let changed = false;
          for (const t of res.tasks) {
            if (t.sceneId != null && next[t.taskId] !== t.sceneId) {
              next[t.taskId] = t.sceneId;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
        setTotal(res.page?.totalElements ?? res.total);
        setPage({
          current: res.page?.current ?? current,
          pageSize: res.page?.pageSize ?? pageSize,
        });
      } catch {
        /* axios 拦截器已提示 */
      } finally {
        setLoading(false);
      }
    },
    [jobId, buildParams, message],
  );

  useEffect(() => {
    loadStatusList();
  }, [loadStatusList]);

  useEffect(() => {
    if (!jobId || Number.isNaN(jobId)) return;
    (async () => {
      try {
        const res = await getPredictionUpdateJobs({ jobId, current: 1, pageSize: 1 });
        const j = res.tasks?.[0];
        setJobMeta(j ?? null);
        setJobName(j?.jobName || '');
        setJobDispatchId(j?.dispatchJobId || '');
      } catch {
        setJobMeta(null);
        setJobName('');
        setJobDispatchId('');
      }
    })();
  }, [jobId]);

  // 本页恒为 PR_DATA，jobId 落定后旁挂拉一次 {taskId: 迭代状态}；带 alive 守卫防竞态。
  useEffect(() => {
    if (!jobId || Number.isNaN(jobId)) return undefined;
    let alive = true;
    getPredictionUpdateTaskIterations(jobId).then((m) => {
      if (alive) setPredictionIterMap(m);
    });
    return () => {
      alive = false;
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId || Number.isNaN(jobId)) return;
    loadTaskList(1, page.pageSize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleSearch = () => {
    loadTaskList(1, page.pageSize);
  };

  const handleReset = () => {
    setFilterTaskId(undefined);
    setFilterSceneName('');
    setFilterStatus(undefined);
    if (!jobId || Number.isNaN(jobId)) return;
    setLoading(true);
    getTaskList({ jobId, current: 1, pageSize: page.pageSize })
      .then((res) => {
        setData(res.tasks);
        setTotal(res.page?.totalElements ?? res.total);
        setPage({
          current: 1,
          pageSize: res.page?.pageSize ?? page.pageSize,
        });
      })
      .finally(() => setLoading(false));
  };

  const handleViewTaskLog = async (taskId: number) => {
    setCurrentTaskId(taskId);
    setTaskLogModalVisible(true);
    setTaskLogData(null);
    setTaskLogLoading(true);
    try {
      const logData = await getTaskLogs(taskId);
      setTaskLogData(logData);
    } catch {
      /* axios 拦截器已提示 */
    } finally {
      setTaskLogLoading(false);
    }
  };

  const handleTaskLogModalCancel = () => {
    setTaskLogModalVisible(false);
    setTaskLogData(null);
    setCurrentTaskId(null);
  };

  const handleCopyReproduceCommand = (sceneName: string) => {
    const cmd = `python3 /home/caros/tools/simulation_tools/scene_run.py --name ${sceneName || ''}`;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(cmd).then(
        () => message.success('已复制本地仿真命令'),
        () => message.error('复制失败'),
      );
      return;
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = cmd;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) message.success('已复制本地仿真命令');
      else message.error('复制失败');
    } catch {
      message.error('复制失败');
    }
  };

  // ── 发起任务:勾选 / 当前查询 ──────────────────────────────────────────────
  /** 勾选任务 → 场景 id(按已加载页的 taskId→sceneId 映射换算,跨页保留)。 */
  const selectedSceneIds = selectedTaskIds
    .map((id) => sceneIdByTaskId[id])
    .filter((v): v is number => typeof v === 'number');

  /** 当前查询条件快照(不含 jobId/current/pageSize),与后端 TaskList SQL builder 同口径。 */
  const currentQueryFilters = toQueryFilters(buildParams(1, 10));
  const currentFilterEntries = extractNonEmptyFilters(currentQueryFilters, { statusList });

  const openRerun = (source: 'selected' | 'filter') => {
    setRerunSource(source);
    setRerunConfirmOpen(true);
  };

  const columns: ColumnsType<TaskListItem> = [
    { title: 'Task ID', dataIndex: 'taskId', width: 100 },
    {
      title: '场景名称',
      dataIndex: 'sceneName',
      width: 200,
      ellipsis: true,
      render: (text: string | null | undefined, record: TaskListItem) => {
        const display = text || '-';
        // 名称点击跳「场景标注」单场景视图,与 FullSceneList 同款交互(target
        // 新页面,不打断当前列表 / 抽屉状态)。onClick stopPropagation:防止冒泡
        // 到 Table 行的展开逻辑。
        const link = record.sceneId ? (
          <Link
            to={`/scene-annotate?sceneId=${record.sceneId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              color: '#5e6ad2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {display}
          </Link>
        ) : (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</span>
        );
        const isConfidential = (record.securityLevel ?? 0) > 0;
        if (!isConfidential) return link;
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {link}
            <Tooltip title="保密：仅具备查看权限的角色可见">
              <LockFilled style={{ color: '#cf1322', fontSize: 12, flexShrink: 0 }} />
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: 'API_CODE',
      dataIndex: 'apiCode',
      width: 160,
      ellipsis: true,
      // 复用 @components/ApiCodeLink：jira(XX-\d+) → Jira browse；其它 →
      // Case-Flow(按 region 派生 waypulse 域名)；空值 → 灰色占位。
      render: (v: string | null | undefined, record: TaskListItem) => (
        <ApiCodeLink code={v} source={record.source} region={record.region} />
      ),
    },
    { title: '场景 ID', dataIndex: 'sceneId', width: 100 },
    {
      title: '状态', dataIndex: 'statusName', width: 110,
      render: (_: string, record: TaskListItem) => (
        <JobStatusTag status={record.status} statusName={record.statusName} />
      ),
    },
    {
      title: '迭代状态',
      key: 'predictionIterStatus',
      width: 96,
      align: 'center',
      render: (_: unknown, record: TaskListItem) => {
        const st = predictionIterMap[String(record.taskId)];
        if (st?.status === 'succeeded' || st?.version) {
          return <Tag color="green" style={{ margin: 0 }}>已迭代</Tag>;
        }
        if (st?.status === 'failed') {
          return (
            <Tooltip title={st.reason || '迭代失败（原因未记录）'}>
              <Tag color="red" style={{ margin: 0, cursor: 'help' }}>迭代失败</Tag>
            </Tooltip>
          );
        }
        if (PREDICTION_TERMINAL_STATUSES.has(record.status)) {
          return <Tag style={{ margin: 0 }}>未迭代</Tag>;
        }
        return <Tag color="blue" style={{ margin: 0 }}>进行中</Tag>;
      },
    },
    {
      title: '迭代版本号',
      key: 'predictionIterVersion',
      width: 200,
      align: 'center',
      render: (_: unknown, record: TaskListItem) => {
        const ver = predictionIterMap[String(record.taskId)]?.version;
        if (!ver) return '—';
        return (
          <Tooltip title={ver}>
            <span style={{
              fontFamily: 'monospace', display: 'inline-block', maxWidth: '100%',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              verticalAlign: 'bottom',
            }}>{ver}</span>
          </Tooltip>
        );
      },
    },
    { title: '任务类型', dataIndex: 'taskTypeName', width: 120 },
    { title: '创建时间', dataIndex: 'createTime', width: 180, render: formatTime },
    { title: '结束时间', dataIndex: 'endTime', width: 180, render: formatTime },
    { title: '创建人', dataIndex: 'creator', width: 100 },
    {
      title: '操作',
      key: 'action',
      width: 88,
      align: 'center',
      fixed: 'right',
      render: (_: unknown, record: TaskListItem) => (
        <Popover
          content={(
            <div style={{ minWidth: 220 }}>
              <div style={{ marginBottom: 8 }}>
                <a
                  onClick={() => handleViewTaskLog(record.taskId)}
                  style={{ color: '#5e6ad2', cursor: 'pointer' }}
                >
                  Task日志
                </a>
              </div>
              <div style={{ marginBottom: 8 }}>
                调度任务ID:
                {' '}
                {record.taskDispatchId || jobDispatchId || '-'}
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  本地仿真命令：
                  {`python3 /home/caros/tools/simulation_tools/scene_run.py --name ${record.sceneName || ''}`}
                </span>
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopyReproduceCommand(record.sceneName)}
                />
              </div>
            </div>
          )}
          trigger="click"
        >
          <Button size="small" icon={<MoreOutlined />} />
        </Popover>
      ),
    },
  ];

  if (!jobId || Number.isNaN(jobId)) {
    return (
      <div style={{ padding: '24px 32px' }}>
        <Text type="danger">无效的 Job ID</Text>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/prediction-update')}
        >
          返回数据更新
        </Button>
      </Space>
      <Title level={2} style={{ marginBottom: 4 }}>
        预测数据迭代 · Task 列表
      </Title>
      <Text type="secondary">
        Job ID: {jobId}
        {jobName ? ` · ${jobName}` : ''}
      </Text>

      <Card style={{ marginTop: 20 }}>
        <Space wrap style={{ marginBottom: 16 }}>
          <InputNumber
            placeholder="Task ID"
            value={filterTaskId}
            onChange={(v) => setFilterTaskId(v ?? undefined)}
            onPressEnter={handleSearch}
            style={{ width: 130 }}
            min={1}
          />
          <Input
            placeholder="场景名称"
            value={filterSceneName}
            onChange={(e) => setFilterSceneName(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="状态"
            allowClear
            style={{ width: 160 }}
            value={filterStatus}
            onChange={(v) => setFilterStatus(v)}
            options={statusList.map((s) => ({
              value: s.value,
              label: s.label ?? String(s.value),
            }))}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
          {/* 两个发起入口:勾选行(手动点选) / 当前查询条件。两者都在后端建匿名
              场景集叶子后下发,弹窗内只配置版本/参数,场景池只读。 */}
          <Tooltip title={selectedSceneIds.length > 0 ? `按勾选的 ${selectedSceneIds.length} 个场景发起新任务` : '请先在下方勾选任务'}>
            <span style={{ display: 'inline-block' }}>
              <Button
                icon={<CheckSquareOutlined />}
                disabled={selectedSceneIds.length === 0}
                onClick={() => openRerun('selected')}
              >
                按勾选任务发起任务{selectedSceneIds.length > 0 ? ` (${selectedSceneIds.length})` : ''}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={total > 0 ? `按当前查询命中的 ${total} 个场景发起新任务` : '当前查询无命中任务'}>
            <span style={{ display: 'inline-block' }}>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                disabled={total === 0}
                onClick={() => openRerun('filter')}
              >
                按当前查询发起任务{total > 0 ? ` (${total})` : ''}
              </Button>
            </span>
          </Tooltip>
        </Space>

        <Table<TaskListItem>
          rowKey="taskId"
          columns={columns}
          dataSource={data}
          loading={loading}
          rowSelection={{
            selectedRowKeys: selectedTaskIds,
            preserveSelectedRowKeys: true,
            onChange: (keys) => setSelectedTaskIds(keys.map(Number)),
          }}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: page.current,
            pageSize: page.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100', '1000'],
            showTotal: (t) => `共 ${t} 条`,
            onChange: (c, s) => loadTaskList(c, s ?? page.pageSize),
          }}
        />
      </Card>

      {/* 「发起任务」两步流:confirm 核对场景池 → CreatePredictionUpdateJobModal
          配置版本/参数,场景池锁定为勾选任务或当前查询快照(后端建匿名场景集)。 */}
      <RerunFromQueryConfirmModal
        open={rerunConfirmOpen && rerunSource != null}
        onCancel={() => setRerunConfirmOpen(false)}
        onConfirm={() => {
          setRerunConfirmOpen(false);
          setRerunCreateOpen(true);
        }}
        title={rerunSource === 'selected' ? '基于勾选任务创建任务' : '基于当前筛选项创建任务'}
        jobId={jobId}
        jobName={jobMeta?.jobName || jobName || undefined}
        sceneCount={rerunSource === 'selected' ? selectedSceneIds.length : total}
        taskCount={rerunSource === 'selected' ? selectedTaskIds.length : total}
        filters={rerunSource === 'selected'
          ? [{
            key: 'taskId',
            label: '已勾选 Task',
            valueLabel: selectedTaskIds.length > 10
              ? `${selectedTaskIds.slice(0, 10).map((id) => `#${id}`).join(', ')} 等 ${selectedTaskIds.length} 个`
              : selectedTaskIds.map((id) => `#${id}`).join(', '),
          }]
          : currentFilterEntries}
      />
      {rerunSource != null && (
        <CreatePredictionUpdateJobModal
          open={rerunCreateOpen}
          onClose={() => setRerunCreateOpen(false)}
          onSuccess={() => {
            setRerunCreateOpen(false);
            setSelectedTaskIds([]);
            message.success('任务已创建');
            loadTaskList(1, page.pageSize);
          }}
          copyRecord={jobMeta}
          byTaskQuery={rerunSource === 'selected'
            ? {
              jobId,
              filters: {},
              sceneIds: selectedSceneIds,
              filterEntries: [{
                key: 'taskId',
                label: '勾选任务',
                valueLabel: `${selectedSceneIds.length} 个场景`,
              }],
              jobName: jobMeta?.jobName || jobName || undefined,
              sceneCount: selectedSceneIds.length,
            }
            : {
              jobId,
              filters: currentQueryFilters,
              filterEntries: currentFilterEntries,
              jobName: jobMeta?.jobName || jobName || undefined,
              sceneCount: total,
            }}
        />
      )}

      <Modal
        title={`Task 日志 - Task ID: ${currentTaskId ?? ''}`}
        open={taskLogModalVisible}
        onCancel={handleTaskLogModalCancel}
        footer={[
          <Button key="close" onClick={handleTaskLogModalCancel}>
            关闭
          </Button>,
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        <Spin spinning={taskLogLoading}>
          {taskLogData && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>日志下载链接：</strong>
                  {taskLogData.logDownloadUrl ? (
                    <a
                      href={taskLogData.logDownloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#5e6ad2', marginLeft: 8 }}
                    >
                      {taskLogData.logDownloadUrl}
                    </a>
                  ) : (
                    <span style={{ marginLeft: 8, color: '#9b9da3' }}>无</span>
                  )}
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>S3路径：</strong>
                  <span style={{ marginLeft: 8 }}>{taskLogData.logS3Path || '-'}</span>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>总行数：</strong>
                  <span style={{ marginLeft: 8 }}>{taskLogData.totalLines || 0}</span>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>显示行数：</strong>
                  <span style={{ marginLeft: 8 }}>
                    {taskLogData.tailLines || 0}
                    （最后
                    {' '}
                    {taskLogData.tailLines || 0}
                    {' '}
                    行）
                  </span>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <strong>日志内容：</strong>
                <div
                  style={{
                    marginTop: 8,
                    padding: 12,
                    backgroundColor: '#18181b',
                    color: '#a1a1aa',
                    borderRadius: 6,
                    border: '1px solid #27272a',
                    maxHeight: 500,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: "'JetBrains Mono', Consolas, monospace",
                    fontSize: 12,
                  }}
                >
                  {taskLogData.logContent || '无日志内容'}
                </div>
              </div>
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
}
